/**
 * EpisodeSubscriptionProvider
 *
 * 为剧集路由提供剧集级 WebSocket 订阅上下文。
 * 挂载时订阅 episode:{episodeId}，卸载时取消订阅。
 * 监听 episodeId 变化，先取消旧订阅再建立新订阅（原子切换）。
 * 监听 isConnected 变化，重连后自动重新订阅。
 *
 * 需求：3.1、3.2、3.4、3.5、3.6、3.7
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';

// ============================================================================
// Context 类型定义
// ============================================================================

interface EpisodeSubscriptionContextType {
  episodeId: string;
  isSubscribed: boolean;
}

// ============================================================================
// Context 创建
// ============================================================================

const EpisodeSubscriptionContext = createContext<EpisodeSubscriptionContextType | null>(null);

export { EpisodeSubscriptionContext };

// ============================================================================
// Provider Props
// ============================================================================

interface EpisodeSubscriptionProviderProps {
  episodeId: string;
  children: React.ReactNode;
}

// ============================================================================
// EpisodeSubscriptionProvider 组件
// ============================================================================

const EpisodeSubscriptionProvider: React.FC<EpisodeSubscriptionProviderProps> = ({
  episodeId,
  children,
}) => {
  const { client, isConnected } = useWebSocketContext();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // 未连接或 client 不存在时，不执行订阅
    if (!isConnected || !client) {
      setIsSubscribed(false);
      return;
    }

    let cancelled = false;

    // 订阅 episode 房间（需求 3.1）
    client
      .subscribe({ type: 'episode', episodeId })
      .then(() => {
        if (!cancelled) {
          setIsSubscribed(true);
        }
      })
      .catch((error: unknown) => {
        // 需求 3.6：订阅失败时记录警告日志，降级为轮询
        console.warn(
          `[EpisodeSubscriptionProvider] 订阅 episode:${episodeId} 失败，降级为轮询:`,
          error
        );
      });

    // cleanup：episodeId 变化或卸载时取消旧订阅（需求 3.2、3.7）
    // useEffect cleanup 先于下一次 effect 执行，确保原子切换：
    // 1. cleanup 取消旧 episodeId 的订阅
    // 2. 新 effect 建立新 episodeId 的订阅
    return () => {
      cancelled = true;
      setIsSubscribed(false);
      client.unsubscribeEpisode(episodeId).catch((error: unknown) => {
        console.warn(
          `[EpisodeSubscriptionProvider] 取消订阅 episode:${episodeId} 失败:`,
          error
        );
      });
    };
  }, [client, isConnected, episodeId]);

  const contextValue: EpisodeSubscriptionContextType = {
    episodeId,
    isSubscribed,
  };

  return (
    <EpisodeSubscriptionContext.Provider value={contextValue}>
      {children}
    </EpisodeSubscriptionContext.Provider>
  );
};

export default EpisodeSubscriptionProvider;

// ============================================================================
// useEpisodeSubscription Hook
// ============================================================================

/**
 * 消费 EpisodeSubscriptionContext 的 hook。
 * 必须在 EpisodeSubscriptionProvider 内部使用，否则抛出错误（需求 3.5）。
 */
export function useEpisodeSubscription(): EpisodeSubscriptionContextType {
  const context = useContext(EpisodeSubscriptionContext);
  if (context === null) {
    throw new Error(
      'useEpisodeSubscription must be used within EpisodeSubscriptionProvider'
    );
  }
  return context;
}
